/*
 |-----------------------------------------------------------------------------
 | Derivación y aplicación del tema
 |-----------------------------------------------------------------------------
 | Un único sitio donde el color de un estudio se convierte en interfaz.
 |
 | Antes había dos: `useSession.applyBranding()` para el panel y un `useEffect`
 | dentro de `GalleryPage` para el portal. Los dos escribían las mismas dos
 | variables y ninguno comprobaba nada. Divergir era cuestión de tiempo.
 */

import {
  fitAccent,
  oklchToRgb,
  parseHex,
  pickForeground,
  rgbToOklch,
  toChannels,
  type Oklch,
  type Rgb,
} from './color'
import { rememberBranding } from './storage'
import {
  DEFAULT_BRAND_COLOR,
  STATUS_HUES,
  THEMES,
  isTenantTheme,
  isThemeName,
  type ThemeChoice,
  type ThemeDefinition,
  type ThemeName,
} from './themes'

export interface Branding {
  logo_url?: string
  primary_color?: string
  /** Color de la marca de selección (la estrella de «elegida»). */
  accent_color?: string
  /** Elección del estudio. Ausente = `auto`, que respeta al visitante. */
  theme?: string
}

export interface ThemeReport {
  theme: ThemeName
  mode: 'light' | 'dark'
  /** false cuando el color del estudio no cumple contraste en este tema. */
  accessible: boolean
  /** Contraste de la etiqueta sobre el botón primario. Objetivo: ≥ 4,5. */
  textRatio: number
  /** Contraste del botón primario sobre la página. Objetivo: ≥ 3. */
  surfaceRatio: number
  /** Cuánto se movió la luminosidad de la marca para que funcionara. */
  lightnessShift: number
}

/* -------------------------------------------------------------------------- */
/* Derivación                                                                  */
/* -------------------------------------------------------------------------- */

/** Tintas candidatas para el texto sobre un color sólido. */
const INKS: readonly Rgb[] = [
  [255, 255, 255],
  [12, 14, 18],
]

/**
 * El fondo del visor a pantalla completa.
 *
 * Es un CONTEXTO DE CONTRASTE distinto y hay que tratarlo aparte: el visor es
 * casi negro pase lo que pase con el tema, porque ahí lo que se juzga es la
 * foto. Un acento ajustado contra un tema claro se apagaría encima —el mismo
 * naranja que destaca sobre blanco desaparece sobre negro— así que los
 * controles que van sobre la imagen usan una variante propia.
 */
const MEDIA_BACKDROP: Rgb = [11, 11, 13]

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

/**
 * Convierte un color de marca y un tema en el mapa completo de variables CSS.
 *
 * Puro: no toca el DOM. Así se puede comprobar en un test o en un script sin
 * navegador, que es como se verificó la tabla de contrastes.
 */
export function deriveTheme(
  brandHex: string | undefined,
  selectHex: string | undefined,
  definition: ThemeDefinition,
): { variables: Record<string, string>; report: Omit<ThemeReport, 'theme' | 'mode'> } {
  const brandRgb = parseHex(brandHex ?? '') ?? parseHex(DEFAULT_BRAND_COLOR)!
  const brand = rgbToOklch(brandRgb)

  const { neutrals, neutralChroma, trackL, accentRange, accentSubtleL, statusL } = definition
  const isDark = definition.mode === 'dark'

  /* Los grises heredan el MATIZ de la marca con croma mínimo. Cuatro líneas, y
     es lo que separa la marca blanca de verdad de un logo pegado encima. */
  const neutral = (l: number): Rgb =>
    oklchToRgb({ l, c: l > 0.98 || l < 0.03 ? 0 : neutralChroma, h: brand.h })

  const surface = neutral(neutrals.surface)

  /* La pista de un medidor. Croma casi nulo a propósito: es la ausencia de
     medida, y con el matiz de la marca una barra vacía se lee como llena. */
  const track = oklchToRgb({ l: trackL, c: 0.003, h: brand.h })

  /* El acento se busca contra las superficies ya calculadas: el mismo azul no
     necesita la misma luminosidad sobre blanco que sobre casi negro. Van las
     DOS —página y pista— porque el acento se pinta sobre ambas, y ajustar sólo
     contra la página dejaba el relleno de las barras por debajo de 3:1 con las
     marcas claras, que son las que quedan al filo del umbral contra el blanco. */
  const fit = fitAccent(brand, [surface, track], INKS, accentRange)
  const fitted = rgbToOklch(fit.accent)

  /* Hover y activo se alejan del fondo, no «se oscurecen»: en un tema oscuro
     oscurecer un botón al pasar por encima lo hace desaparecer. */
  const direction = isDark ? 1 : -1
  const shift = (delta: number): Rgb =>
    oklchToRgb({ ...fitted, l: clamp01(fitted.l + delta * direction) })

  const status = (hue: number): Record<string, string> => {
    const base = oklchToRgb({ l: statusL, c: 0.14, h: hue })
    const subtle = oklchToRgb({
      l: isDark ? neutrals.surface + 0.06 : 0.955,
      c: isDark ? 0.03 : 0.035,
      h: hue,
    })
    const line = oklchToRgb({ l: isDark ? 0.4 : 0.85, c: 0.07, h: hue })

    return {
      base: toChannels(base),
      contrast: toChannels(pickForeground(base, INKS)),
      subtle: toChannels(subtle),
      line: toChannels(line),
    }
  }

  const positive = status(STATUS_HUES.positive)
  const warning = status(STATUS_HUES.warning)
  const danger = status(STATUS_HUES.danger)

  /* El color de selección es el segundo de la marca. Se ajusta igual que el
     primario porque cumple el mismo papel: fondo sólido con un icono encima. */
  const selectSource: Oklch = (() => {
    const parsed = parseHex(selectHex ?? '')

    /*
     | Sin segundo color, se deriva girando el matiz 55°.
     |
     | Eran 150°, y el comentario lo llamaba «complementario suave» cuando está
     | a un paso del opuesto: con una marca granate la estrella de favorita
     | salía VERDE, y con una rosa también. Un estudio que eligió su color veía
     | aparecer otro que no había pedido y que no pega con nada suyo.
     |
     | 55° es un análogo: se distingue —sigue siendo otro color, que es el
     | punto— pero pertenece a la misma familia. Y en el caso que más se ve,
     | una marca cálida, cae en oro, que es justo lo que una estrella de
     | favorita debería ser.
     */
    return parsed ? rgbToOklch(parsed) : { ...brand, h: (brand.h + 55) % 360 }
  })()

  const selectFit = fitAccent(selectSource, [surface], INKS, accentRange)

  /* Sobre el visor el rango es siempre el de un fondo oscuro, independiente del
     tema: el fondo del visor no cambia con él. */
  const MEDIA_RANGE = [0.58, 0.86] as const
  const accentOverMedia = fitAccent(brand, [MEDIA_BACKDROP], INKS, MEDIA_RANGE)
  const selectOverMedia = fitAccent(selectSource, [MEDIA_BACKDROP], INKS, MEDIA_RANGE)

  const variables: Record<string, string> = {
    /* Superficies */
    '--surface': toChannels(surface),
    '--surface-raised': toChannels(neutral(neutrals.raised)),
    '--surface-sunken': toChannels(neutral(neutrals.sunken)),

    /* Líneas */
    '--border': toChannels(neutral(neutrals.border)),
    '--border-strong': toChannels(neutral(neutrals.borderStrong)),

    /* La pista de un medidor. Croma casi nulo a propósito: es la ausencia de
       medida, y con el matiz de la marca una barra vacía se lee como llena. */
    '--track': toChannels(track),

    /* Texto */
    '--text': toChannels(neutral(neutrals.text)),
    '--text-muted': toChannels(neutral(neutrals.textMuted)),
    '--text-subtle': toChannels(neutral(neutrals.textSubtle)),

    /* Acento — el color del estudio, a un brillo que funciona */
    '--accent': toChannels(fit.accent),
    '--accent-hover': toChannels(shift(0.06)),
    '--accent-active': toChannels(shift(0.11)),
    '--accent-contrast': toChannels(fit.foreground),
    /* El croma NO es constante: se topa con el del propio acento.
       Con una marca viva `fitted.c` está muy por encima de 0,045 y no cambia
       nada. Pero el tema de la web pública tiene un acento de TINTA, y forzarle
       croma 0,045 sacaba un melocotón saturado que contradecía el tema entero y
       era indistinguible de `warning-subtle` — una opción elegida y un aviso se
       leían igual. */
    '--accent-subtle': toChannels(
      oklchToRgb({ ...fitted, l: accentSubtleL, c: Math.min(0.045, fitted.c) }),
    ),

    /* Selección — la estrella de «elegida» */
    '--select': toChannels(selectFit.accent),
    '--select-contrast': toChannels(selectFit.foreground),

    /* Los mismos dos colores, pero ajustados para ir ENCIMA de una foto */
    '--accent-media': toChannels(accentOverMedia.accent),
    '--accent-media-contrast': toChannels(accentOverMedia.foreground),
    '--select-media': toChannels(selectOverMedia.accent),
    '--select-media-contrast': toChannels(selectOverMedia.foreground),

    /* Estado */
    '--positive': positive.base!,
    '--positive-contrast': positive.contrast!,
    '--positive-subtle': positive.subtle!,
    '--positive-line': positive.line!,
    '--warning': warning.base!,
    '--warning-contrast': warning.contrast!,
    '--warning-subtle': warning.subtle!,
    '--warning-line': warning.line!,
    '--danger': danger.base!,
    '--danger-contrast': danger.contrast!,
    '--danger-subtle': danger.subtle!,
    '--danger-line': danger.line!,

    /* Foco: siempre el acento, que ya tiene contraste garantizado con la página */
    '--focus': toChannels(fit.accent),

    /*
     | Compatibilidad con las pantallas todavía sin migrar.
     |
     | Se siguen emitiendo para que el panel y la web funcionen durante toda la
     | migración, pero ahora salen del motor y ya llevan el contraste corregido:
     | una pantalla vieja con `bg-[var(--brand-primary)]` mejora sin tocarla.
     |
     | Van en `rgb(...)` completo y no en canales sueltos a propósito. Los
     | tokens nuevos son canales porque Tailwind los necesita así para aplicar
     | alfa, pero `bg-[var(--brand-primary)]` inyecta el valor tal cual y
     | `background-color: 37 99 235` no es CSS válido. Se borran cuando no
     | quede ningún uso.
     */
    '--brand-primary': `rgb(${toChannels(fit.accent)})`,
    '--brand-accent': `rgb(${toChannels(selectFit.accent)})`,
    '--brand-surface': `rgb(${toChannels(surface)})`,
    '--brand-text': `rgb(${toChannels(neutral(neutrals.text))})`,
  }

  return {
    variables,
    report: {
      accessible: fit.accessible,
      textRatio: fit.textRatio,
      surfaceRatio: fit.surfaceRatio,
      lightnessShift: Math.abs(fitted.l - brand.l),
    },
  }
}

/* -------------------------------------------------------------------------- */
/* Aplicación al documento                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Resuelve `auto` contra la preferencia del sistema.
 *
 * El estudio puede FIJAR el tema —una galería oscura tiene que verse oscura en
 * el móvil de un cliente que lleva el sistema en claro, porque es su entrega y
 * la ha elegido así— o dejarlo en `auto` y respetar al visitante.
 */
function resolve(choice: ThemeChoice): ThemeName {
  if (isThemeName(choice)) return choice

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'galeria' : 'editorial'
}

let active: { branding: Branding; choice: ThemeChoice } | null = null
let media: MediaQueryList | null = null

/**
 * La barra del navegador, del color del estudio.
 *
 * `theme-color` vive en `index.html`, que es el MISMO documento para la web
 * pública y para el portal de un cliente: no hay forma de acertar ahí con un
 * valor fijo. Estaba en el azul por defecto de `--accent` —o sea, en el color
 * que cada estudio cambia— y luego en nuestro arcilla de marca. Las dos veces,
 * un cliente que abría su galería desde el móvil veía la barra pintada con un
 * color que no era el de su fotógrafo.
 *
 * Se escribe la SUPERFICIE del tema resuelto, no el acento. El acento es color
 * de botón; la barra del navegador es la continuación de la página, y en una
 * galería oscura una franja con el color de marca parte la pantalla en dos.
 * Con la superficie, la barra desaparece y la foto ocupa todo — que es de lo
 * que va el visor.
 *
 * El favicon NO se toca, y no es un olvido. El logo del estudio se normaliza a
 * borde máximo 512 conservando la proporción (`NormalizeLogoService`), así que
 * un logo apaisado metido en 16 px es ilegible; y se sirve por URL FIRMADA con
 * TTL, mientras que el navegador cachea el favicon mucho más allá de esa
 * caducidad y acabaría pidiendo una URL muerta. Un favicon por tenant necesita
 * un recorte cuadrado propio y una URL estable, que es otro trabajo.
 */
function paintChrome(surface: string): void {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')

  if (meta === null) {
    // No debería faltar —está en `index.html`— pero crearla es más barato que
    // depender de que nadie la quite.
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }

  meta.content = `rgb(${surface})`
}

function paint(): ThemeReport | null {
  if (active === null) return null

  const name = resolve(active.choice)
  const definition = THEMES[name]
  const { variables, report } = deriveTheme(
    active.branding.primary_color,
    active.branding.accent_color,
    definition,
  )

  const root = document.documentElement

  for (const [property, value] of Object.entries(variables)) {
    root.style.setProperty(property, value)
  }

  root.dataset.theme = name
  // Alinea los controles nativos, las barras de scroll y el autorrelleno del
  // navegador con el tema. Sin esto, en una galería oscura el desplegable de
  // contraseñas de Chrome sale blanco y canta.
  root.style.colorScheme = definition.mode

  paintChrome(variables['--surface']!)

  return { theme: name, mode: definition.mode, ...report }
}

/**
 * Aplica el tema de un estudio y lo mantiene sincronizado.
 *
 * Devuelve el informe de contraste para que los ajustes puedan avisar al
 * estudio si su color no cumple, en vez de publicar en silencio una galería
 * que su cliente no puede leer.
 */
export function applyTheme(
  branding: Branding | null | undefined,
  /**
   * `false` para las vistas previas de los ajustes.
   *
   * Sin esto, juguetear con el selector de color guardaría cada tono por el que
   * pasa el arrastre, y al recargar sin haber guardado el estudio vería el
   * último color que probó en vez del suyo.
   */
  remember = true,
): ThemeReport | null {
  const resolved: Branding = branding ?? {}
  // `isTenantTheme` y no `isThemeName`: `marketing` es nuestro tema, no uno que
  // un estudio pueda tener elegido. Si apareciera en su branding —la API ya no
  // lo acepta, pero una fila vieja podría traerlo— se trata como `auto`.
  const choice: ThemeChoice = isTenantTheme(resolved.theme) ? resolved.theme : 'auto'

  active = { branding: resolved, choice }

  if (remember) rememberBranding(resolved)

  if (media === null) {
    media = window.matchMedia('(prefers-color-scheme: dark)')
    // Sólo repinta si el estudio dejó `auto`; con un tema fijo la preferencia
    // del visitante no manda.
    media.addEventListener('change', () => {
      if (active !== null && !isThemeName(active.choice)) paint()
    })
  }

  return paint()
}
